import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../api/client';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { Header } from '../components/Header';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { useAppStore } from '../store/useAppStore';
import { useSwipeBack } from '../hooks/useSwipeBack';
import './SecurityScreen.css';

interface OAuthProvider {
  provider: string;
  provider_email: string;
  provider_name: string;
  provider_picture?: string;
  created_at: string;
  last_used_at: string;
}

export const SecurityScreen: React.FC = () => {
  const { setCurrentPage } = useAppStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [oauthProviders, setOauthProviders] = useState<OAuthProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasTelegramId, setHasTelegramId] = useState<boolean>(false);
  const [telegramId, setTelegramId] = useState<number | null>(null);
  
  // Состояния для модального окна пароля
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Состояния для привязки Telegram
  const [showTelegramLinkModal, setShowTelegramLinkModal] = useState(false);
  const [telegramLinkCode, setTelegramLinkCode] = useState<string>('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  
  // Получаем данные пользователя из Telegram WebApp
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const userId = tgUser?.id || null;

  useEffect(() => {
    loadSecurityData();
    
    // Автоматически обновляем данные при возврате в приложение
    // (например, после привязки Telegram)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Страница стала видимой, обновляем данные безопасности...');
        loadSecurityData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Загружаем статус пароля
      const passwordStatus = await apiClient.get<{ has_password: boolean }>('/api/auth/password-status');
      setHasPassword(passwordStatus.has_password);
      
      // Загружаем OAuth провайдеры
      const { providers } = await apiClient.getOAuthProviders();
      setOauthProviders(providers);
      
      // Загружаем информацию о пользователе из БД (включая telegram_id)
      const userInfo = await apiClient.getUserInfo();
      
      // Если telegram_id появился, закрываем модальное окно привязки
      if (userInfo.has_telegram_id && showTelegramLinkModal) {
        setShowTelegramLinkModal(false);
        setTelegramLinkCode('');
        alert('✅ Telegram успешно привязан к вашему аккаунту!');
      }
      
      setHasTelegramId(userInfo.has_telegram_id);
      setTelegramId(userInfo.telegram_id || null);
      
      console.log('👤 User info:', {
        user_id: userInfo.user_id,
        has_telegram_id: userInfo.has_telegram_id,
        telegram_id: userInfo.telegram_id
      });
    } catch (error: any) {
      console.error('Error loading security data:', error);
      setError('Не удалось загрузить данные безопасности');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    setPasswordError('');
    
    if (!passwordInput || !confirmPasswordInput) {
      setPasswordError('Заполните оба поля');
      return;
    }
    
    if (passwordInput !== confirmPasswordInput) {
      setPasswordError('Пароли не совпадают');
      return;
    }
    
    if (passwordInput.length < 8) {
      setPasswordError('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    const hasLetters = /[a-zA-Z]/.test(passwordInput);
    const hasNumbers = /[0-9]/.test(passwordInput);
    
    if (!hasLetters || !hasNumbers) {
      setPasswordError('Пароль должен содержать буквы и цифры');
      return;
    }
    
    try {
      setIsSettingPassword(true);
      await apiClient.post('/api/auth/set-password', { password: passwordInput });
      setHasPassword(true);
      setShowPasswordModal(false);
      setPasswordInput('');
      setConfirmPasswordInput('');
      alert(hasPassword ? 'Пароль успешно изменен! Старые токены недействительны.' : 'Пароль успешно установлен!');
    } catch (error: any) {
      console.error('Error setting password:', error);
      setPasswordError(error.response?.data?.error || 'Ошибка при установке пароля');
    } finally {
      setIsSettingPassword(false);
    }
  };


  const handleUnlinkProvider = async (provider: string) => {
    if (!confirm(`Отвязать ${provider}?`)) return;
    
    try {
      await apiClient.unlinkOAuthProvider(provider);
      await loadSecurityData();
      alert(`${provider} успешно отвязан`);
    } catch (error: any) {
      if (error.response?.data?.code === 'LAST_AUTH_METHOD') {
        alert('❌ Нельзя отвязать последний способ входа. Сначала установите пароль.');
      } else {
        alert(error.message || 'Не удалось отвязать провайдер');
      }
    }
  };

  const handleGenerateTelegramLink = async () => {
    try {
      setIsGeneratingCode(true);
      const result = await apiClient.generateTelegramLinkCode();
      setTelegramLinkCode(result.code);
      setShowTelegramLinkModal(true);
      
      // Открываем бота в Telegram через deep link
      const botUsername = 'alveon_finance_bot'; // [[memory:10445476]]
      const deepLink = `https://t.me/${botUsername}?start=link_${result.code}`;
      window.open(deepLink, '_blank');
      
    } catch (error: any) {
      alert(error.message || 'Не удалось создать код привязки');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const getProviderDisplayName = (provider: string): string => {
    const names: Record<string, string> = {
      google: 'Google',
      apple: 'Apple',
      facebook: 'Facebook',
    };
    return names[provider] || provider;
  };

  // Свайп назад
  useSwipeBack(containerRef, () => setCurrentPage('settings'));

  if (loading) {
    return (
      <div ref={containerRef} className="security-screen page">
        <Header title="Безопасность и вход" onBack={() => setCurrentPage('settings')} />
        <div className="security-loading">
          <Icon name="settings" size={48} color="var(--color-primary)" />
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary)' }}>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="security-screen page">
      <Header title="Безопасность и вход" onBack={() => setCurrentPage('settings')} />
      
      {error && (
        <div className="security-error">
          <Icon name="alert-circle" size={24} color="#FF3B30" />
          <span>{error}</span>
        </div>
      )}

      {/* Секция: Пароль */}
      <div className="section">
        <h3 className="section__title">Пароль</h3>
        <div className="section__items">
          <Card padding="none" className="neo-raised">
            <button
              className="settings-item"
              onClick={() => {
                setPasswordError('');
                setPasswordInput('');
                setConfirmPasswordInput('');
                setShowPasswordModal(true);
              }}
            >
              <div className="settings-item__left">
                <span className="settings-item__icon">
                  <Icon name="lock" size={20} />
                </span>
                <span className="settings-item__label">
                  {hasPassword ? 'Изменить пароль' : 'Установить пароль'}
                </span>
              </div>
              <div className="settings-item__right">
                <span className="settings-item__value">
                  {hasPassword ? '••••••' : 'Не установлен'}
                </span>
                <Icon name="chevron-right" size={16} color="#C7C7CC" />
              </div>
            </button>
          </Card>
        </div>
      </div>

      {/* Секция: Способы входа */}
      <div className="section">
        <h3 className="section__title">Способы входа</h3>
        <div className="section__items" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          {/* Telegram */}
          {hasTelegramId && (
            <Card padding="none" className="neo-raised">
              <div className="settings-item" style={{ cursor: 'default' }}>
                <div className="settings-item__left">
                  <span className="settings-item__icon" style={{ 
                    background: 'linear-gradient(135deg, #54A9EB 0%, #2E8DD6 100%)'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                    </svg>
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="settings-item__label">Telegram</span>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                      ID: {telegramId || userId || '—'}
                    </span>
                  </div>
                </div>
                <div style={{ 
                  padding: '8px 16px', 
                  fontSize: '14px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  color: '#34C759',
                  borderRadius: '12px',
                  fontWeight: '600'
                }}>
                  Привязан
                </div>
              </div>
            </Card>
          )}

          {/* Google */}
          {oauthProviders.find(p => p.provider === 'google') ? (
            <Card padding="none" className="neo-raised">
              <div className="settings-item" style={{ cursor: 'default' }}>
                <div className="settings-item__left">
                  <span className="settings-item__icon" style={{ background: 'transparent' }}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="settings-item__label">Google</span>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                      {oauthProviders.find(p => p.provider === 'google')?.provider_email}
                    </span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnlinkProvider('google');
                  }}
                  style={{ 
                    padding: '8px 16px', 
                    fontSize: '14px',
                    background: 'rgba(255, 59, 48, 0.1)',
                    color: '#FF3B30'
                  }}
                >
                  Отвязать
                </Button>
              </div>
            </Card>
          ) : hasTelegramId ? (
            <Card padding="md" className="neo-raised">
              <GoogleSignInButton 
                mode="link"
                onError={(error) => alert(error)}
              />
            </Card>
          ) : (
            <Card padding="lg" className="neo-raised neo-static">
              <div style={{ textAlign: 'center' }}>
                <Icon name="info" size={48} color="var(--color-text-tertiary)" style={{ marginBottom: '12px', opacity: 0.6 }} />
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--color-text-secondary)', 
                  marginBottom: '8px',
                  lineHeight: '1.5'
                }}>
                  Привязка Google доступна только для пользователей, зарегистрированных через Telegram
                </p>
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--color-text-tertiary)', 
                  lineHeight: '1.4'
                }}>
                  Telegram используется как первичный способ авторизации
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Секция: Telegram (для OAuth пользователей) */}
      {!hasTelegramId && (
        <div className="section">
          <h3 className="section__title">Telegram</h3>
          <div className="section__items">
            <Card padding="md" className="neo-raised">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #54A9EB 0%, #2E8DD6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Привязать Telegram</div>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      Используйте Telegram для входа в приложение
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleGenerateTelegramLink}
                  disabled={isGeneratingCode}
                  style={{ 
                    width: '100%',
                    background: 'linear-gradient(135deg, #54A9EB 0%, #2E8DD6 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {isGeneratingCode ? 'Генерация кода...' : 'Привязать Telegram'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Информационный блок */}
      <div className="security-info-box">
        <div className="security-info-icon">
          <Icon name="info" size={20} color="var(--color-primary)" />
        </div>
        <div className="security-info-text">
          <h4>О безопасности</h4>
          <p>
            Вы можете использовать несколько способов входа одновременно. 
            Все ваши данные остаются синхронизированными между устройствами.
          </p>
        </div>
      </div>

      {/* Модальное окно установки/изменения пароля */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          if (!isSettingPassword) {
            setShowPasswordModal(false);
            setPasswordError('');
            setPasswordInput('');
            setConfirmPasswordInput('');
          }
        }}
        title={hasPassword ? 'Изменить пароль' : 'Установить пароль'}
        size="auto"
        position="bottom"
      >
        <div style={{ padding: '4px 0' }}>
          <p style={{ color: '#8E8E93', fontSize: '14px', marginBottom: '20px', lineHeight: '1.4' }}>
            {hasPassword 
              ? 'Введите новый пароль. После изменения все старые сессии будут завершены.'
              : `Установите пароль для входа через email или Telegram ID: ${userId}`
            }
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
              {hasPassword ? 'Новый пароль' : 'Пароль'}
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Минимум 8 символов"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: passwordError ? '2px solid #FF3B30' : 'none',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'var(--neo-bg)',
                color: 'var(--color-text-primary)',
                boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)',
              }}
              disabled={isSettingPassword}
            />
          </div>
          
          <div style={{ marginBottom: passwordError ? '8px' : '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
              Подтвердите пароль
            </label>
            <input
              type="password"
              value={confirmPasswordInput}
              onChange={(e) => setConfirmPasswordInput(e.target.value)}
              placeholder="Повторите пароль"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: passwordError ? '2px solid #FF3B30' : 'none',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'var(--neo-bg)',
                color: 'var(--color-text-primary)',
                boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)',
              }}
              disabled={isSettingPassword}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isSettingPassword) {
                  handleSetPassword();
                }
              }}
            />
          </div>
          
          {passwordError && (
            <p style={{ color: '#FF3B30', fontSize: '13px', marginBottom: '16px' }}>
              {passwordError}
            </p>
          )}
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError('');
                setPasswordInput('');
                setConfirmPasswordInput('');
              }}
              disabled={isSettingPassword}
              style={{ flex: 1 }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleSetPassword}
              disabled={isSettingPassword || !passwordInput || !confirmPasswordInput}
              style={{ flex: 1 }}
            >
              {isSettingPassword ? 'Сохранение...' : (hasPassword ? 'Изменить' : 'Установить')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно с кодом привязки Telegram */}
      <Modal
        isOpen={showTelegramLinkModal}
        onClose={() => {
          setShowTelegramLinkModal(false);
          setTelegramLinkCode('');
          loadSecurityData(); // Перезагружаем данные для проверки привязки
        }}
        title="Привязка Telegram"
        size="auto"
        position="bottom"
      >
        <div style={{ padding: '4px 0', textAlign: 'center' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #54A9EB 0%, #2E8DD6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
            </div>
          </div>
          
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px', marginBottom: '20px', lineHeight: '1.5' }}>
            Откройте Telegram бота, и он автоматически привяжется к вашему аккаунту. Код действителен 15 минут.
          </p>
          
          <div style={{ 
            background: 'var(--neo-bg)',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginBottom: '8px' }}>
              Код привязки:
            </div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              letterSpacing: '8px',
              color: 'var(--color-primary)'
            }}>
              {telegramLinkCode}
            </div>
          </div>

          <Button
            onClick={() => {
              const botUsername = 'alveon_finance_bot';
              const deepLink = `https://t.me/${botUsername}?start=link_${telegramLinkCode}`;
              window.open(deepLink, '_blank');
            }}
            style={{ 
              width: '100%',
              background: 'linear-gradient(135deg, #54A9EB 0%, #2E8DD6 100%)',
              color: 'white',
              border: 'none'
            }}
          >
            Открыть в Telegram
          </Button>
        </div>
      </Modal>
    </div>
  );
};

